import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY_PREFIX = 'ss_screening_state';
const LEGACY_STORAGE_KEY = 'ss_screening_state';

interface Pattern {
  name:        string;
  type:        string;
  strength:    number;
  description: string;
}

interface Judgment {
  buy_score: number;
  sell_score: number;
  verdict: string;
  verdict_kind?: 'bullish' | 'bearish' | 'neutral';
  confidence?: number;
  pattern_score?: number;
  source?: 'rule' | 'ml' | 'ml_blend' | 'rule_low_ml_conf' | string;
  ml_model?: string | null;
  ml_confidence?: number | null;
  ml_enabled?: boolean;
  model_loaded?: boolean;
  ml_status?: string | null;
  ml_error?: string | null;
}

export interface ScanResult {
  ticker:      string;
  close:       number;
  diff:        number;
  pct:         number;
  rsi:         number | null;
  macd:        number | null;
  ma25_dev:    number | null;
  vol_ratio:   number;
  patterns:    Pattern[];
  judgment?: Judgment;
  expectation: Judgment;
  trade_analysis?: { bias: 'bullish' | 'bearish' | 'neutral'; score: number; confidence: number; summary: string };
  credit_ratio?: number | null;
  credit_assessment?: { label: string; short_label: string; tone: 'up' | 'down' | 'neutral' } | null;
  date:        string;
  ai_comment?: string;
}

export type ScanMode    = 'all' | 'bullish' | 'bearish';
export type ScanErrKind = 'timeout' | 'network' | 'server' | 'empty' | null;

export interface ScanStatus {
  job_id?:     string | null;
  status:      'idle' | 'queued' | 'running' | 'done' | 'error' | 'cancelled';
  total:       number;
  done:        number;
  hit_cache:   number;
  new_scanned: number;
  skipped:     number;
  pct:         number;
  error:       string | null;
  universe_total?: number;
  cached_ready?: number;
  scan_seconds?: number | null;
  scan_run_id?: string | null;
  last_progress_at?: number | null;
  active_workers?: number;
  failed_count?: number;
  last_failure_reason?: string | null;
  stalled?: boolean;
  seconds_since_progress?: number | null;
  ml_enabled?: boolean;
  model_loaded?: boolean;
  model_name?: string | null;
  model_status?: string | null;
  model_error?: string | null;
  model_trained_at?: string | null;
  model_schema_version?: string | null;
  model_metrics?: Record<string, any> | null;
  model_feature_count?: number | null;
  model_classes?: number[] | null;
  mode?: string | null;
  universe_mode?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  requested_at?: string | null;
  queue_position?: number | null;
  estimated_wait_seconds?: number | null;
  priority?: number | null;
  throttled_reason?: string | null;
  active_job_count?: number | null;
  worker?: string | null;
}

interface ScreeningState {
  results:    ScanResult[];
  mode:       ScanMode;
  scanned:    boolean;
  resultJobId: string | null;
  scanTime:   string;
  fetchedAt:  string;
  scanCount:  number;
  errorKind:  ScanErrKind;
  expanded:   string | null;
  detailData: Record<string, any>;

  // Paging
  offset:      number;
  totalReady:  number;
  hasMore:     boolean;
  loadingPage: boolean;

  // Background scan progress
  scanStatus:  ScanStatus | null;
}

interface ScreeningCtx extends ScreeningState {
  setMode:         (m: ScanMode) => void;
  setResults:      (r: ScanResult[], meta: { scanTime: string; scanCount: number; totalReady: number; hasMore: boolean; jobId?: string | null }) => void;
  appendResults:   (r: ScanResult[], meta: { totalReady: number; hasMore: boolean }) => void;
  updateQuotes:    (q: Record<string, { close: number; diff: number; pct: number }>) => void;
  setError:        (e: ScanErrKind) => void;
  setExpanded:     (t: string | null) => void;
  addDetail:       (t: string, d: any) => void;
  setLoadingPage:  (b: boolean) => void;
  setScanStatus:   (s: ScanStatus | null) => void;
  incOffset:       (by: number) => void;
  resetPaging:     () => void;
  // Why: モード切替時の "古いカードを残さない" 用。errorKind を立てずに
  //      results だけを空にする（empty エラー画面に誤誘導しないため）。
  clearResultsForReload: () => void;
  reset:           () => void;
}

const initial: ScreeningState = {
  results: [], mode: 'bullish', scanned: false, resultJobId: null,
  scanTime: '', fetchedAt: '', scanCount: 0,
  errorKind: null, expanded: null, detailData: {},
  offset: 0, totalReady: 0, hasMore: false, loadingPage: false,
  scanStatus: null,
};

const storageKeyForScope = (scope: string): string => `${STORAGE_KEY_PREFIX}:${scope}`;

const hydrate = (scope: string): ScreeningState => {
  try {
    const raw = sessionStorage.getItem(storageKeyForScope(scope));
    if (!raw) return initial;
    const parsed = JSON.parse(raw);

    // Why: スキャン結果0件のまま scanned=true を引き継ぐと「スキャン結果 0/0件」画面が
    //      初回表示として残ってしまうため、実行中でも結果が無いわけでもないなら
    //      スプラッシュ（"AIスキャン開始" ボタン）にフォールバックする。
    const status = parsed.scanStatus?.status;
    const hasResults = Array.isArray(parsed.results) && parsed.results.length > 0;
    const hasLocalScan = Boolean(parsed.scanned || parsed.resultJobId || hasResults);
    const isActive = hasLocalScan && (status === 'running' || status === 'queued');
    const shouldShowSplash = !isActive && !hasResults;

    return {
      ...initial,
      ...parsed,
      loadingPage: false,
      // Keep a hydrated running status for the first paint. AIScan re-syncs with
      // the server on mount, but dropping to idle here briefly renders the top
      // screen before the results view is restored.
      scanStatus: parsed.scanStatus,
      ...(shouldShowSplash
        ? { scanned: false, resultJobId: null, errorKind: null, scanStatus: null, scanTime: '', scanCount: 0 }
        : {}),
    };
  } catch {
    return initial;
  }
};

const ScreeningContext = createContext<ScreeningCtx | null>(null);

export const ScreeningProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const storageScope = isAuthenticated && user?.id ? `user:${user.id}` : 'guest';
  const [state, setState] = useState<ScreeningState>(() => hydrate('guest'));
  const [hydratedScope, setHydratedScope] = useState('guest');

  useEffect(() => {
    if (hydratedScope !== storageScope) return;
    try {
      sessionStorage.setItem(storageKeyForScope(storageScope), JSON.stringify(state));
    } catch { /* quota exceeded — ignore */ }
  }, [hydratedScope, state, storageScope]);

  useEffect(() => {
    setState(hydrate(storageScope));
    setHydratedScope(storageScope);
    try {
      sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* no-op */
    }
  }, [storageScope]);

  const setMode = useCallback((mode: ScanMode) => {
    setState(s => ({ ...s, mode }));
  }, []);

  const setResults = useCallback((results: ScanResult[], meta: { scanTime: string; scanCount: number; totalReady: number; hasMore: boolean; jobId?: string | null }) => {
    setState(s => ({
      ...s,
      results,
      resultJobId: meta.jobId !== undefined ? meta.jobId : s.resultJobId,
      scanTime:  meta.scanTime,
      fetchedAt: new Date().toISOString(),
      scanCount: meta.scanCount,
      scanned:   true,
      errorKind: results.length === 0 ? 'empty' : null,
      offset:    results.length,
      totalReady: meta.totalReady,
      hasMore:   meta.hasMore,
    }));
  }, []);

  const appendResults = useCallback((more: ScanResult[], meta: { totalReady: number; hasMore: boolean }) => {
    setState(s => {
      const seen = new Set(s.results.map(r => r.ticker));
      const deduped = more.filter(r => !seen.has(r.ticker));
      const next = [...s.results, ...deduped];
      return {
        ...s,
        results: next,
        offset: next.length,
        totalReady: meta.totalReady,
        hasMore: meta.hasMore,
      };
    });
  }, []);

  const updateQuotes = useCallback((q: Record<string, { close: number; diff: number; pct: number }>) => {
    setState(s => ({
      ...s,
      results: s.results.map(r => {
        const fresh = q[r.ticker];
        if (!fresh) return r;
        return { ...r, close: fresh.close, diff: fresh.diff, pct: fresh.pct };
      }),
    }));
  }, []);

  const setError = useCallback((errorKind: ScanErrKind) => {
    setState(s => ({ ...s, errorKind }));
  }, []);

  const setExpanded = useCallback((expanded: string | null) => {
    setState(s => ({ ...s, expanded }));
  }, []);

  const addDetail = useCallback((t: string, d: any) => {
    setState(s => ({
      ...s,
      detailData: { ...s.detailData, [t]: { ...(s.detailData[t] || {}), ...d } },
    }));
  }, []);

  const setLoadingPage = useCallback((loadingPage: boolean) => {
    setState(s => ({ ...s, loadingPage }));
  }, []);

  const setScanStatus = useCallback((scanStatus: ScanStatus | null) => {
    setState(s => ({ ...s, scanStatus }));
  }, []);

  const incOffset = useCallback((by: number) => {
    setState(s => ({ ...s, offset: s.offset + by }));
  }, []);

  const resetPaging = useCallback(() => {
    setState(s => ({
      ...s,
      results: [], resultJobId: null, offset: 0, totalReady: 0, hasMore: false,
      scanned: false, errorKind: null, expanded: null, detailData: {},
    }));
  }, []);

  const clearResultsForReload = useCallback(() => {
    setState(s => ({
      ...s,
      results: [], resultJobId: null, offset: 0, totalReady: 0, hasMore: false,
      errorKind: null, expanded: null,
    }));
  }, []);

  const reset = useCallback(() => setState(initial), []);

  // Why: Provider value をインラインオブジェクトで作ると毎 render で新インスタンスとなり、
  // useContext で受け取る consumer 側の `useCallback(_, [scr])` / `useEffect(_, [scr])`
  // が無駄に再生成される。state が同一なら value も同一にして useEffect の連鎖再走を防ぐ。
  // 各 action は useCallback で安定参照済みのため、deps は state のみで十分。
  const value = useMemo(
    () => ({
      ...state,
      setMode, setResults, appendResults, updateQuotes,
      setError, setExpanded, addDetail,
      setLoadingPage, setScanStatus, incOffset, resetPaging, clearResultsForReload, reset,
    }),
    [state, setMode, setResults, appendResults, updateQuotes, setError, setExpanded, addDetail, setLoadingPage, setScanStatus, incOffset, resetPaging, clearResultsForReload, reset],
  );

  return (
    <ScreeningContext.Provider value={value}>
      {children}
    </ScreeningContext.Provider>
  );
};

export function useScreening(): ScreeningCtx {
  const ctx = useContext(ScreeningContext);
  if (!ctx) throw new Error('useScreening must be used inside ScreeningProvider');
  return ctx;
}
